<template>
	<o-card
		v-if="orderItem"
		class="order-dashboard-home-order-item"
		@click="setup.gotToOrder(orderItem.id)">
		<list-item-bubbles-arrow v-bind="{ id: orderItem.id }"/>
		<div class="order-dashboard-home-order-item__header">
			<o-icon-section font-icon="icon-shopping-cart-1">
				<p>Commande n° {{ orderItem.id }}</p>
				<strong>{{ orderItem.professionalSkill?.label }}</strong>
			</o-icon-section>
			<o-icon-section font-icon="icon-interface-bookmark-tag">
				<p>Créée le {{ new Date(orderItem.createdDate).toLocaleDateString() }}</p>
				<p>Par <strong>{{ setup.creator.name }} ({{ setup.creator.city }})</strong></p>
				<p>Pour <strong>{{ setup.orderFor.name }} ({{ setup.orderFor.city }})</strong></p>
			</o-icon-section>
			<o-icon-section
				font-icon="icon-interface-calendar-mark">
				<div :class="{ 'text--warning': orderItem.type === ORDER_DASHBOARD_FILTER.Urgent }">
					<strong>{{ new Date(orderItem.start).toLocaleDateString() }} à {{ orderItem.city }}</strong>
					<p>{{ setup.daysOffset }}</p>
				</div>
			</o-icon-section>
		</div>
		<div class="order-dashboard-home-order-item__content">
			<div class="order-dashboard-home-order-item__pending-tempWorker">
				<p>En attente de réponse candidats ({{ orderItem.candidatesWaitingResponses.length }})</p>
				<div
					v-overflow-counter
					class="order-dashboard-home-order-item__container order-dashboard-home-order-item__container--avatars">
					<o-avatar
						v-for="candidate in orderItem.candidatesWaitingResponses"
						:key="candidate.userId"
						v-tooltip="candidate.userName"
						:name="candidate.userName"
						:avatar="candidate.userAvatarId"
						size="xs"
						root-url="/app/avatar/"/>
				</div>
			</div>
			<div class="order-dashboard-home-order-item__pending-client">
				<p>En attente de réponse clients ({{ orderItem.clientsWaitingResponses.length }})</p>
				<div
					v-overflow-counter
					class="order-dashboard-home-order-item__container order-dashboard-home-order-item__container--avatars">
					<o-avatar
						v-for="candidate in orderItem.clientsWaitingResponses"
						:key="candidate.userId"
						v-tooltip="candidate.userName"
						:name="candidate.userName"
						:avatar="candidate.userAvatarId"
						size="xs"
						root-url="/app/avatar/"/>
				</div>
			</div>
			<div class="order-dashboard-home-order-item__waiting-validation">
				<p>Candidats pouvant être validés ({{ orderItem.candidatesWhoCanBeValidated.length }})</p>
				<div
					v-overflow-counter
					class="order-dashboard-home-order-item__container order-dashboard-home-order-item__container--avatars">
					<o-avatar
						v-for="candidate in orderItem.candidatesWhoCanBeValidated"
						:key="candidate.userId"
						v-tooltip="candidate.userName"
						:name="candidate.userName"
						:avatar="candidate.userAvatarId"
						size="xs"
						root-url="/app/avatar/"/>
				</div>
			</div>
		</div>
		<div class="order-dashboard-home-order-item__actions">
			<p
				v-if="orderItem.histories.length"
				class="order-dashboard-home-order-item__expand-btn"
				@click.stop="setup.toggleExpandLastActivity()">
				Dernières activités <o-icon :font-icon="setup.expand ? 'icon-interface-arrows-button-up' : 'icon-interface-arrows-button-down'"/>
				<o-chips
					outline
					:color="orderItem.histories.length ? 'danger' : 'default'">
					{{ orderItem.histories.length }}
				</o-chips>
			</p>
			<p
				v-else
				class="order-dashboard-home-order-item__expand-btn order-dashboard-home-order-item__expand-btn--empty">
				Aucune activité
			</p>
			<o-label
				v-if="setup.orderStatusType"
				:color="setup.orderStatusType.color">
				{{ setup.orderStatusType.label }}
			</o-label>
		</div>
		<div
			:ref="setup._history"
			class="order-dashboard-home-order-item__last-activity"
			:class="{ 'order-dashboard-home-order-item__last-activity--expanded': setup.expand }"
			:style="setup.expand ? `max-height: ${setup.historyHeight}px` : 'max-height: 0rem'">
			<div class="order-dashboard-home-order-item__container">
				<order-dashboard-home-history-item
					v-for="history in setup.orderhistoriesSorted"
					:key="history.id"
					:history="history"/>
			</div>
		</div>
	</o-card>
</template>

<script setup lang="ts">
import OrderDashboardHomeOrderItemSetupService from '@/setup/order/OrderDashboardHomeOrderItemSetupService';
import OrderDashboardHomeHistoryItem from '@/components/order/OrderDashboardHomeHistoryItem.vue';
import { ORDER_DASHBOARD_FILTER } from '@/typings/enums/ORDER_DASHBOARD_FILTER';
const props = defineProps(OrderDashboardHomeOrderItemSetupService.props);
const setup = new OrderDashboardHomeOrderItemSetupService(props);
</script>

<style scoped lang="less">
@import '@/styles/variables';
@import '@/styles/mixins';

.order-dashboard-home-order-item {
	@media @phone {
		max-width: 100%;
	}

  &__header {
    display: flex;
    gap: 1.3rem;
    align-items: center;
    margin-bottom: 2.5rem;

    .orion-icon{
      margin-left: unset;
    }
    .orion-icon-section + .orion-icon-section {
      margin: unset;
    }
    .orion-icon-section:nth-child(1){
      flex: 1 0 9.3rem;
    }
    .orion-icon-section:nth-child(2){
      flex: 1 0 12.5rem;
    }
    .orion-icon-section:nth-child(3){
      flex: 1 0 7.5rem;
    }

    p{
      margin: unset;
    }

		@media @phone {
			flex-direction: column;
			align-items: flex-start;

			.orion-icon-section:nth-child(1),
			.orion-icon-section:nth-child(2),
			.orion-icon-section:nth-child(3) {
				flex: unset;
			}
		}
  }

  &__content {
    display: flex;
    gap: 1.3rem;
    margin-bottom: 1.9rem;

		@media @phone {
			flex-direction: column;
		}
  }

  &__pending-client, &__pending-tempWorker, &__waiting-validation {
    flex: 1;
		max-width: calc((100% - 2.5rem) / 3);

		.v-overflow-counter__counter {
			transform: translateX(0.8rem);
			font-weight: 500;
			color: var(--grey-darker);
		}

		@media @phone {
			max-width: unset;
		}
  }

  &__container {
    border-radius: 0.3rem;
    background: var(--grey-lighter);
    padding: 0.6rem;
		&--avatars {
			display: flex;
			min-height: 3.1rem;
			.orion-avatar {
				border: 0.13rem solid var(--grey-lighter);
				&:not(&:first-child) {
					margin-left: -0.6rem;
				}
			}
		}
  }

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.6rem;
	}

  &__expand-btn{
    cursor: pointer;
    &:hover {
			opacity: 0.8;
    }
		margin: unset;
		.orion-icon{
			margin:0 0.6rem 0 0.4rem;
		}

		&--empty {
			pointer-events: none;
		}
  }

	&__router-btn {
		font-size: 1.25rem;
	}

  &__last-activity{
    max-height: 0;
    overflow: hidden;
    transition: max-height .2s ease;

    &--expanded {
      height: 100%;
      max-height: 18.75rem;
    }
  }
}
</style>
